import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "../../icons";
import { Storefront as StorefrontIcon } from "../../icons";
import { db } from "../../firebase";
import { brandColors } from "../../theme";
import { pageContentSx } from "../../pageLayout";
import { T } from "../Translate";
import StoresAppBar from "./StoresAppBar";
import GoogleAdSideRail from "../../components/GoogleAdSideRail";
import GoogleAdInline from "../../components/GoogleAdInline";

import SearchClearAdornment from "../../components/SearchClearAdornment";
// Directorio público de tiendas: se ve sin iniciar sesión (las tiendas son de
// lectura pública en las reglas). Cada tarjeta es un <a href> real para que
// Google pueda seguir los links y rastrear cada tienda (ver también
// sitemap.xml, generado por la función `sitemap`).
const StoresDirectory = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    let active = true;
    db.collection("stores")
      .get()
      .then((snap) => {
        if (!active) return;
        const items = snap.docs
          .map((doc) => ({ slug: doc.id, ...doc.data() }))
          // Tiendas sin nombre (incompletas o de prueba) no se listan.
          .filter((store) => store.storeName)
          .sort((a, b) => (a.storeName || a.slug).localeCompare(b.storeName || b.slug));
        setStores(items);
      })
      .catch((e) => console.error("Error cargando el directorio de tiendas:", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const term = searchTerm.trim().toLowerCase();
  const filteredStores = stores.filter((store) =>
    `${store.storeName || ""} ${store.description || ""} ${store.address || ""}`
      .toLowerCase()
      .includes(term)
  );

  return (
    <Box sx={{ bgcolor: brandColors.backgroundDefault, minHeight: "100vh", pb: "80px" }}>
      <StoresAppBar title={<T>Tiendas</T>} subtitle={<T>Tiendas en Notiline</T>} quickNav={false} />
      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, pt: { xs: 6, md: 10 }, position: "relative" }}>
        {/* AdSense: solo con tiendas en pantalla (no durante la carga ni sin resultados) */}
        {!loading && filteredStores.length > 0 && <GoogleAdSideRail />}
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            <T>Tiendas en Notiline</T>
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            <T>Conocé los negocios que venden en Notiline y sus productos.</T>
          </Typography>
        </Stack>

        <TextField
          fullWidth
          size="small"
          placeholder="Buscar tienda..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: <SearchClearAdornment value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : filteredStores.length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>
            <T>No hay resultados.</T>
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {filteredStores.map((store) => (
              <Grid item xs={12} sm={6} key={store.slug}>
                <Card
                  sx={{
                    height: "100%",
                    backgroundColor: brandColors.backgroundPaper,
                    border: `1px solid ${brandColors.divider}`,
                    borderRadius: 3,
                  }}
                >
                  <CardActionArea
                    component={RouterLink}
                    to={`/stores/${store.slug}`}
                    sx={{ height: "100%", alignItems: "stretch" }}
                  >
                    {store.bannerUrl && (
                      <Box
                        component="img"
                        src={store.bannerUrl}
                        alt={store.storeName || store.slug}
                        loading="lazy"
                        sx={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                      />
                    )}
                    <CardContent>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={store.logoUrl || undefined} sx={{ bgcolor: brandColors.primary }}>
                          <StorefrontIcon />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" component="h2" noWrap>
                            {store.storeName || store.slug}
                          </Typography>
                          {store.address && (
                            <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap component="div">
                              {store.address}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      {store.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1.5,
                            color: "text.secondary",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {store.description}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        {!loading && filteredStores.length > 0 && <GoogleAdInline />}
      </Container>
    </Box>
  );
};

export default StoresDirectory;
